import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Users, UserPlus, NotebookText, BookOpen, Apple, Activity, AlertTriangle, Calendar, KeyRound, Copy, Mail, MailCheck, MailX, LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otps, setOtps] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const { user } = useAuth();

  const loadNotifs = () => {
    api.get("/dietitian/active-otps").then(({ data }) => setOtps(data)).catch(() => {});
    api.get("/dietitian/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/dietitian/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    loadNotifs();
    const interval = setInterval(loadNotifs, 10000);
    return () => clearInterval(interval);
  }, []);

  const copy = async (text, msg = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Copy failed"); }
  };

  if (loading) return <div className="text-muted-foreground">Loading dashboard…</div>;
  if (!data) return null;

  const stats = [
    { label: "Total Patients", value: data.total_patients, icon: Users, testid: "stat-total-patients" },
    { label: "Active This Week", value: data.active_patients, icon: Activity, testid: "stat-active-patients" },
    { label: "Not Following", value: data.inactive_patients, icon: AlertTriangle, testid: "stat-inactive-patients" },
    { label: "Diet Plans", value: data.total_plans, icon: NotebookText, testid: "stat-total-plans" },
    { label: "Recipes", value: data.total_recipes, icon: BookOpen, testid: "stat-total-recipes" },
    { label: "Foods", value: data.total_foods, icon: Apple, testid: "stat-total-foods" },
  ];

  return (
    <div className="space-y-8" data-testid="dietitian-dashboard">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Dashboard</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Welcome, {user?.name?.split(" ")[0] || "Doctor"}</h1>
        <p className="text-muted-foreground mt-2">Here's a snapshot of your practice today.</p>
      </header>

      {/* Active OTPs */}
      {otps.length > 0 && (
        <div className="nt-card p-5 border-primary/40 bg-secondary/40" data-testid="active-otps-card">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-primary" />
            <h2 className="font-semibold">Active patient login codes</h2>
            <span className="text-xs text-muted-foreground">(share with the patient)</span>
          </div>
          <div className="mt-4 divide-y divide-border">
            {otps.map((o) => (
              <div key={o.otp_id} className="py-3 flex items-center justify-between gap-3" data-testid={`otp-row-${o.otp_id}`}>
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {o.patient_name || "Patient"} <span className="text-xs text-muted-foreground font-mono">({o.patient_number})</span>
                    {o.email_sent ? (
                      <span title="Emailed to patient" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" data-testid={`otp-email-sent-${o.otp_id}`}>
                        <MailCheck size={10} /> Emailed
                      </span>
                    ) : (
                      <span title={o.email_error || "Email not sent — share manually"} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400" data-testid={`otp-email-failed-${o.otp_id}`}>
                        <MailX size={10} /> Not sent
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{o.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <code className="text-xl font-mono font-bold tracking-[0.3em] bg-card border border-border rounded-lg px-3 py-1.5" data-testid={`otp-code-${o.otp_id}`}>{o.code}</code>
                  <button onClick={() => copy(o.code, "Code copied")} className="text-muted-foreground hover:text-primary" data-testid={`copy-otp-${o.otp_id}`}>
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patient recovery notifications */}
      {notifs.length > 0 && (
        <div className="nt-card p-5" data-testid="patient-notifications-card">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-primary" />
            <h2 className="font-semibold">Patient account requests (last 24h)</h2>
            <span className="text-xs text-muted-foreground">(recovery & password actions — pass to the patient)</span>
          </div>
          <div className="mt-4 divide-y divide-border">
            {notifs.map((n) => (
              <div key={n.notification_id} className="py-3 flex items-center justify-between gap-3" data-testid={`notif-row-${n.notification_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {n.type === "number_recovery" && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">Forgot number</span>}
                    {n.type === "password_reset" && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">Password reset</span>}
                    {n.type === "password_set_confirmation" && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Password set</span>}
                    <span>{n.patient_name || "Patient"}</span>
                    {n.patient_number && <span className="text-xs text-muted-foreground font-mono">({n.patient_number})</span>}
                    {(n.type === "password_reset" || n.type === "number_recovery") && (
                      n.email_sent ? (
                        <span title="Emailed to patient" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" data-testid={`notif-email-sent-${n.notification_id}`}>
                          <MailCheck size={10} /> Emailed
                        </span>
                      ) : (
                        <span title={n.email_error || "Email not sent — share manually"} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400" data-testid={`notif-email-failed-${n.notification_id}`}>
                          <MailX size={10} /> Not sent
                        </span>
                      )
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{n.email} • {n.message}</div>
                  {n.reset_url && (
                    <div className="text-xs text-primary mt-1 truncate font-mono" data-testid={`reset-url-${n.notification_id}`}>{n.reset_url}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {n.type === "number_recovery" && n.patient_number && (
                    <button onClick={() => copy(n.patient_number, "Patient number copied")} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`copy-number-${n.notification_id}`}>
                      <Copy size={12} /> Copy number
                    </button>
                  )}
                  {n.type === "password_reset" && n.reset_url && (
                    <button onClick={() => copy(n.reset_url, "Reset link copied")} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`copy-reset-${n.notification_id}`}>
                      <LinkIcon size={12} /> Copy link
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/patients?new=1" className="nt-card p-5 hover:border-primary transition-colors flex items-center gap-3" data-testid="quick-add-patient">
          <div className="w-10 h-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center"><UserPlus size={18} /></div>
          <div className="text-sm font-semibold">Add patient</div>
        </Link>
        <Link to="/diet-plans/new" className="nt-card p-5 hover:border-primary transition-colors flex items-center gap-3" data-testid="quick-create-plan">
          <div className="w-10 h-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center"><NotebookText size={18} /></div>
          <div className="text-sm font-semibold">Create diet plan</div>
        </Link>
        <Link to="/recipes/new" className="nt-card p-5 hover:border-primary transition-colors flex items-center gap-3" data-testid="quick-create-recipe">
          <div className="w-10 h-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center"><BookOpen size={18} /></div>
          <div className="text-sm font-semibold">Create recipe</div>
        </Link>
        <Link to="/foods" className="nt-card p-5 hover:border-primary transition-colors flex items-center gap-3" data-testid="quick-foods">
          <div className="w-10 h-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center"><Apple size={18} /></div>
          <div className="text-sm font-semibold">Food database</div>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="nt-stat" data-testid={s.testid}>
              <div className="flex items-center justify-between">
                <span className="nt-label">{s.label}</span>
                <Icon size={16} className="text-primary" />
              </div>
              <div className="text-3xl font-bold">{s.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent activity */}
        <div className="nt-card p-6" data-testid="recent-activity-card">
          <h2 className="text-xl font-semibold">Recent meal tracking</h2>
          <p className="text-sm text-muted-foreground mt-1">Latest patient meal logs</p>
          <div className="mt-5 divide-y divide-border">
            {data.recent_activity.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">No activity yet</div>
            ) : (
              data.recent_activity.map((a, i) => (
                <div key={i} className="py-3 flex items-center justify-between" data-testid={`activity-row-${i}`}>
                  <div>
                    <div className="font-medium text-sm">{a.patient_name}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {a.meal_type} • {a.date} • {a.status}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${a.status === "completed" ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {a.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming follow-ups */}
        <div className="nt-card p-6" data-testid="upcoming-followups-card">
          <h2 className="text-xl font-semibold">Upcoming follow-ups</h2>
          <p className="text-sm text-muted-foreground mt-1">Scheduled patient visits</p>
          <div className="mt-5 divide-y divide-border">
            {data.upcoming_followups.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">No follow-ups scheduled</div>
            ) : (
              data.upcoming_followups.map((p, i) => (
                <div key={i} className="py-3 flex items-center gap-3" data-testid={`followup-row-${i}`}>
                  <Calendar size={16} className="text-primary" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{p.name || "Patient"}</div>
                    <div className="text-xs text-muted-foreground">{p.next_followup}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
